// src/features/events/components/MetadataSearchDialog.tsx

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import type { MetadataSearchParams } from '../types';

interface MetadataSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (params: MetadataSearchParams) => void;
  initialParams?: Partial<MetadataSearchParams>;
}

/**
 * Dialog for searching events by metadata key/value
 */
export const MetadataSearchDialog: React.FC<MetadataSearchDialogProps> = ({
  open,
  onClose,
  onSearch,
  initialParams,
}) => {
  const translate = useTranslate();
  const [key, setKey] = useState(initialParams?.key || '');
  const [value, setValue] = useState(initialParams?.value || '');
  const [dateFrom, setDateFrom] = useState(initialParams?.dateFrom || '');
  const [dateTo, setDateTo] = useState(initialParams?.dateTo || '');
  const [error, setError] = useState<string | null>(null);

  const handleSearch = () => {
    if (!key.trim()) {
      setError(translate('resources.events.metadata.keyRequired'));
      return;
    }
    if (!value.trim()) {
      setError(translate('resources.events.metadata.valueRequired'));
      return;
    }

    setError(null);
    onSearch({
      key: key.trim(),
      value: value.trim(),
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    });
    onClose();
  };

  const handleClear = () => {
    setKey('');
    setValue('');
    setDateFrom('');
    setDateTo('');
    setError(null);
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon color="primary" />
          {translate('resources.events.metadata.searchTitle')}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('resources.events.metadata.searchDescription')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Metadata Key */}
          <TextField
            label={translate('resources.events.metadata.key')}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="firmwareVersion"
            required
            fullWidth
          />

          {/* Metadata Value */}
          <TextField
            label={translate('resources.events.metadata.value')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="2.1.0"
            required
            fullWidth
          />

          {/* Date Range */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            {translate('resources.events.metadata.dateRange')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              type="date"
              label={translate('resources.events.filters.dateFrom')}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              type="date"
              label={translate('resources.events.filters.dateTo')}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClear} color="inherit">
          {translate('ra.action.clear_input_value')}
        </Button>
        <Button onClick={handleClose}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSearch}
          startIcon={<SearchIcon />}
        >
          {translate('ra.action.search')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetadataSearchDialog;
